import { PosthogEventType } from "@repo/app-types/client/client";
import {
  asGradingId,
  type GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { useEffect, useRef, type FC } from "react";
import { useParams } from "react-router";
import { usePlaybookGradingQuery } from "../../api/entities/performancePlaybook.js";
import { useViewerAnalytics } from "../teammate/viewerAnalytics.js";

/**
 * Captures one `customer_interaction:view` per grading its route shows, once
 * the grading has loaded. Moving the route to another grading captures again;
 * tab and criterion changes within the same grading do not. PostHog supplies
 * the viewer; `call_teammate_id` is the teammate on the call.
 */
export const CustomerInteractionViewTracker: FC = () => {
  const { gradingId: gradingIdParam } = useParams();
  if (!gradingIdParam) {
    throw new Error("Missing params.");
  }
  const gradingId = asGradingId(gradingIdParam);

  const { currentData: grading } = usePlaybookGradingQuery({ gradingId });
  const { viewerId, captureWithViewer } = useViewerAnalytics();
  const capturedGradingIdRef = useRef<GradingId | null>(null);

  useEffect(() => {
    if (!grading || capturedGradingIdRef.current === grading.id) {
      return;
    }
    captureWithViewer(PosthogEventType.CustomerInteractionView, {
      grading_id: grading.id,
      call_teammate_id: grading.teammate_id,
      is_self_view: viewerId === grading.teammate_id,
    });
    capturedGradingIdRef.current = grading.id;
  }, [captureWithViewer, grading, viewerId]);

  return null;
};
